/**
 * 習慣チェックボックスコンポーネント
 *
 * 習慣の完了/未完了を切り替えるためのインタラクティブなボタン。
 * タップ時のスケールアニメーションとローディング状態をサポート。
 *
 * Rails で言えば:
 * - app/views/habits/_checkbox.html.erb に相当するビューヘルパー
 *
 * @example
 * <HabitCheckbox
 *   checked={habit.completedToday}
 *   onClick={() => handleToggle(habit.id)}
 *   pending={isPending}
 *   color="#22c55e"
 *   size="md"
 * />
 */

import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'

interface HabitCheckboxProps {
  /** 完了状態 */
  checked: boolean
  /** 無効状態（クリック不可） */
  disabled?: boolean
  /** ローディング中（アニメーション表示） */
  pending?: boolean
  /** クリック時のコールバック */
  onClick: () => void
  /** サイズ（xs: 16px, sm: 32px, md: 44px, lg: 56px） */
  size?: 'xs' | 'sm' | 'md' | 'lg'
  /** 追加の CSS クラス */
  className?: string
  /** テーマカラー（CSS カラー値、例: "#3b82f6"） */
  color?: string
  /** 予定外完了を示す（点線枠スタイル） */
  extra?: boolean
}

const sizeClasses = {
  xs: 'w-4 h-4',
  sm: 'w-8 h-8',
  md: 'w-11 h-11',
  lg: 'w-14 h-14',
}

const iconSizes = {
  xs: 8,
  sm: 14,
  md: 20,
  lg: 28,
}

export function HabitCheckbox({
  checked,
  disabled = false,
  pending = false,
  onClick,
  size = 'md',
  className,
  color,
  extra = false,
}: HabitCheckboxProps) {
  const defaultColor = '#3b82f6'
  const activeColor = color || defaultColor

  // 予定外完了時のスタイルを計算
  const getBackgroundColor = () => {
    if (!checked) return 'transparent'
    // 予定外完了は薄い背景
    return extra ? `${activeColor}b3` : activeColor // b3 = 70% opacity
  }

  const getBorderStyle = () => {
    if (extra) {
      return {
        borderStyle: 'dashed' as const,
        borderColor: checked ? activeColor : '#9ca3af', // gray-400
      }
    }
    return {
      borderStyle: 'solid' as const,
      borderColor: activeColor,
    }
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || pending}
      className={cn(
        'relative flex items-center justify-center rounded-full border-2 transition-all duration-150 ease-out',
        size !== 'xs' && 'touch-target',
        sizeClasses[size],
        disabled && 'cursor-not-allowed opacity-40',
        pending && 'animate-pulse',
        !disabled && !pending && 'hover:scale-105 active:scale-95',
        className
      )}
      style={{
        backgroundColor: getBackgroundColor(),
        ...getBorderStyle(),
      }}
      aria-label={
        extra
          ? checked
            ? '追加完了済み'
            : '予定外（クリックで追加完了）'
          : checked
            ? '完了済み'
            : '未完了'
      }
      aria-pressed={checked}
    >
      <Check
        size={iconSizes[size]}
        className={cn(
          'transition-all duration-150 ease-out',
          checked ? 'scale-100 text-white opacity-100' : 'scale-0 opacity-0'
        )}
        style={{ color: checked ? undefined : activeColor }}
        strokeWidth={3}
      />
    </button>
  )
}
